import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Key, Copy, ArrowClockwise, Warning, Code, CheckCircle } from "@phosphor-icons/react";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function CompanyApiIntegration() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/company/api-info");
      setInfo(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const regenerate = async () => {
    if (!window.confirm("Gerar uma nova chave vai INVALIDAR a chave antiga. Todos os bots/sistemas que estão usando vão parar de funcionar até você atualizar. Confirmar?")) return;
    try {
      const { data } = await api.post("/company/api-info/regenerate");
      setInfo(data);
      toast.success("Nova chave gerada");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copy = (val, key) => {
    navigator.clipboard.writeText(val);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  if (loading) return <div className="p-8 text-[#5C5C59]">Carregando…</div>;

  const baseUrl = backend;
  const apiKey = info?.api_key || "";
  const maskedKey = apiKey ? `${apiKey.slice(0, 10)}${"•".repeat(28)}${apiKey.slice(-6)}` : "";
  const curlSend = `curl -X POST "${baseUrl}/api/v1/messages/text" \\
  -H "X-API-Key: ${apiKey}" \\
  -H "Content-Type: application/json" \\
  -d '{"phone":"5511987654321","message":"Olá do bot!"}'`;

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10 space-y-6" data-testid="api-integration-page">
      <div>
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#0F4C3A]">Integrações</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
          <Code size={30} weight="bold" /> API do NetsZap
        </h1>
        <p className="text-sm text-[#5C5C59] mt-2 leading-relaxed">
          Conecte seu próprio sistema ou automação e envie mensagens WhatsApp pela sua conta.
        </p>
      </div>

      {/* URL and Key */}
      <div className="bg-white border-2 border-[#E5E5E0] p-5 md:p-6 space-y-5">
        <div>
          <label className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#5C5C59]">URL Base da API</label>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 bg-[#F7F7F5] border border-[#E5E5E0] px-3 py-2 text-sm font-mono break-all" data-testid="api-base-url">{baseUrl}/api</code>
            <button onClick={() => copy(`${baseUrl}/api`, "url")} className="p-2 border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white transition-colors">
              {copied === "url" ? <CheckCircle size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
            </button>
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#5C5C59] flex items-center gap-2">
            <Key size={12} weight="bold" /> X-API-Key
          </label>
          <div className="mt-1 flex items-center gap-2">
            <code className="flex-1 bg-[#F7F7F5] border border-[#E5E5E0] px-3 py-2 text-sm font-mono break-all" data-testid="api-key-value">{apiKey}</code>
            <button onClick={() => copy(apiKey, "key")} className="p-2 border border-[#0F4C3A] text-[#0F4C3A] hover:bg-[#0F4C3A] hover:text-white transition-colors" data-testid="copy-api-key">
              {copied === "key" ? <CheckCircle size={16} weight="bold" /> : <Copy size={16} weight="bold" />}
            </button>
            <Button onClick={regenerate} variant="outline" className="rounded-none border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white" data-testid="regenerate-api-key">
              <ArrowClockwise size={16} weight="bold" className="mr-1" /> Gerar nova
            </Button>
          </div>
          <div className="mt-2 flex items-start gap-2 text-xs text-[#D97706] bg-[#FFF8E1] border border-[#F5D97A] p-3">
            <Warning size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div>
              <b>Nunca compartilhe essa chave publicamente.</b> Ela permite enviar mensagens em nome da sua conta.
              Ao gerar uma nova, a antiga deixa de funcionar imediatamente.
            </div>
          </div>
        </div>
      </div>

      {/* Endpoints doc */}
      <div className="bg-white border-2 border-[#E5E5E0] p-5 md:p-6 space-y-6">
        <h2 className="text-xl font-black tracking-tight">Endpoints</h2>

        <Endpoint
          method="POST"
          path="/api/v1/messages/text"
          desc="Envia uma mensagem de texto pelo WhatsApp."
          example={curlSend}
          body={`{
  "phone": "5511987654321",
  "message": "Olá do bot!",
  "connection_id": "opcional"
}`}
          testId="ep-send"
        />

        <Endpoint
          method="GET"
          path="/api/v1/connections"
          desc="Lista as conexões WhatsApp ativas da sua conta."
          example={`curl "${baseUrl}/api/v1/connections" -H "X-API-Key: ${apiKey}"`}
          testId="ep-list"
        />

        <Endpoint
          method="GET"
          path="/api/v1/message-status/{wa_message_id}"
          desc="Consulta o status de entrega de uma mensagem enviada."
          example={`curl "${baseUrl}/api/v1/message-status/ABC123" -H "X-API-Key: ${apiKey}"`}
          testId="ep-status"
        />
      </div>

      {/* Auth format */}
      <div className="bg-[#0F4C3A] text-white p-5 md:p-6 space-y-3">
        <h3 className="text-lg font-black tracking-tight">Como autenticar</h3>
        <p className="text-sm text-[#c8d5cf] leading-relaxed">
          Todas as chamadas precisam incluir o header:
        </p>
        <code className="block bg-black/30 px-3 py-2 text-sm font-mono">X-API-Key: {maskedKey}</code>
        <p className="text-xs text-[#c8d5cf]">
          Se você usa uma ferramenta que só aceita <b>Bearer Token</b>, pode passar como
          <code className="bg-black/30 px-1 mx-1">Authorization: Bearer &lt;sua-chave&gt;</code>
          — nós aceitamos ambos.
        </p>
      </div>
    </div>
  );
}

function Endpoint({ method, path, desc, example, body, testId }) {
  const colors = {
    GET: "bg-[#2563EB] text-white",
    POST: "bg-[#059669] text-white",
    DELETE: "bg-[#B91C1C] text-white",
  };
  return (
    <div className="border-l-4 border-[#0F4C3A] pl-4 py-1" data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-2 py-0.5 text-xs font-black tracking-wider ${colors[method] || ""}`}>{method}</span>
        <code className="font-mono text-sm">{path}</code>
      </div>
      <p className="text-sm text-[#5C5C59]">{desc}</p>
      {body && (
        <details className="mt-2">
          <summary className="text-xs text-[#0F4C3A] cursor-pointer font-semibold">Corpo (JSON)</summary>
          <pre className="mt-2 bg-[#F7F7F5] border border-[#E5E5E0] p-3 text-xs font-mono overflow-x-auto">{body}</pre>
        </details>
      )}
      <details className="mt-2">
        <summary className="text-xs text-[#0F4C3A] cursor-pointer font-semibold">Exemplo (curl)</summary>
        <pre className="mt-2 bg-[#1A1A1A] text-[#7FD8B8] p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap">{example}</pre>
      </details>
    </div>
  );
}
